// ----------------------------------------------------------------------------
// BREAKCHOP — Drum break slicer + step sequencer
//
// Drop a loop, auto-detect transients, slice to 16 pads, step sequencer with
// per-slice pitch/reverse/decay/pan. Export WAV, SFZ kit, or REX2.
//
// Reuses:
//   - renderWaveform from @azopsoft/shared/ui (wavedit waveform engine)
//   - legacy-bucket-integration (bucket + export modal)
//   - audiobuffer-to-wav for WAV export
//   - rex2-iff for REX2 file structure
// ----------------------------------------------------------------------------

const { qs, qsa, build, alertmessage } = require('../../libs/qs');
const { renderWaveform } = require('@azopsoft/shared/ui');
const audioBufferToWav = require('audiobuffer-to-wav');
const initLegacyBucket = require('./legacy-bucket-integration.js');

// ---- Theme ----
let currentTheme;
try {
  const storedTheme = localStorage.getItem('currentTheme');
  if (storedTheme) currentTheme = JSON.parse(storedTheme);
} catch (e) {}
if (!currentTheme) currentTheme = window.Theme;
const themeColors = Object.values(currentTheme.data);
window.themeColors = themeColors;
window.currentTheme = currentTheme;
window.CONTROLZ = {};

// ---- State ----
const store = {
  audioBuffer: null,       // decoded AudioBuffer
  fileName: '',
  slices: [],              // [{ start, end, name }] in samples
  pads: [],                // pad objects: { sliceIndex, pitch, reverse, decay, pan, vol } (one per slice)
  pattern: [],             // [step] -> array of pad indices (or null)
  subSteps: {},            // { "step:pad" -> number of subdivisions (0=none, 2,3,4,6,8) }
  numSteps: 16,
  bpm: 120,
  isPlaying: false,
  currentStep: 0,
  loop: true,
  schedulerId: null,
  nextStepTime: 0,
  audioContext: null,
  zoom: 1,
  viewStart: 0,            // 0..1
  viewEnd: 1,              // 0..1
  selectedPad: -1,
};

// Start with no pads; they grow to match the slice count when slices are created
function makePad() {
  return { sliceIndex: -1, pitch: 0, reverse: false, decay: 1.0, pan: 0, vol: 1.0 };
}

// Initialize empty pattern
function initPattern(steps) {
  store.numSteps = steps;
  store.pattern = [];
  store.subSteps = {};
  for (let i = 0; i < steps; i++) store.pattern.push([]);
}
initPattern(16);

// ---- DOM refs ----
const fileInput = qs('#file-input');
const autoSliceBtn = qs('#auto-slice-btn');
const clearSlicesBtn = qs('#clear-slices-btn');
const waveformDropzone = qs('#waveform-dropzone');
const waveformContainer = qs('#waveform-container');
const waveformCanvas = qs('#waveform-canvas');
const sliceMarkers = qs('#slice-markers');
const scrollbar = qs('#waveform-scrollbar');
const scrollbarHandle = qs('#scrollbar-handle');
const sensitivitySlider = qs('#sensitivity');
const minSliceSlider = qs('#min-slice-ms');
const sliceCountEl = qs('#slice-count');
const padsGrid = qs('#pads-grid');
const sequencerGrid = qs('#sequencer-grid');
const playSeqBtn = qs('#play-seq-btn');
const randomSeqBtn = qs('#random-seq-btn');
const clearSeqBtn = qs('#clear-seq-btn');
const bpmInput = qs('#bpm-input');
const stepsSelect = qs('#steps-select');
const saveBtn = qs('#save-btn');
const saveModal = qs('#save-modal');
const saveModalClose = qs('#save-modal-close');
const saveModalCancel = qs('#save-modal-cancel');
const fillSlider = qs('#fill-slider');
const fillValEl = qs('#fill-val');
const chaosSlider = qs('#chaos-slider');
const chaosValEl = qs('#chaos-val');

// ---- Audio context (lazy) ----
function getAudioContext() {
  if (!store.audioContext) {
    store.audioContext = new AudioContext();
  }
  return store.audioContext;
}

// ============================================================================
// FILE LOADING
// ============================================================================

function setupFileLoading() {
  waveformDropzone.addEventListener('click', () => fileInput.click());

  fileInput.addEventListener('change', (e) => {
    const file = e.target.files[0];
    if (file) loadAudioFile(file);
  });

  // Drag & drop onto waveform area — accept both OS file drags and bucket item drags
  const dropTargets = [waveformDropzone, waveformContainer];

  dropTargets.forEach((target) => {
    if (!target) return;

    ['dragenter', 'dragover'].forEach((evt) => {
      target.addEventListener(evt, (e) => {
        // Accept bucket item drags and OS file drags
        const isBucketItem = e.dataTransfer.types.includes('application/x-bucket-item-id');
        const hasFiles = e.dataTransfer.types.includes('Files');
        if (isBucketItem || hasFiles) {
          e.preventDefault();
          // Must use 'copy' — bucket items' dragstart sets effectAllowed='copy'
          e.dataTransfer.dropEffect = 'copy';
          target.classList.add('dragover');
        }
      });
    });

    target.addEventListener('dragleave', (e) => {
      if (e.target === target) {
        target.classList.remove('dragover');
      }
    });

    target.addEventListener('drop', async (e) => {
      e.preventDefault();
      target.classList.remove('dragover');

      // Check for bucket item drop first
      const itemId = e.dataTransfer.getData('application/x-bucket-item-id');
      if (itemId) {
        try {
          if (!breakchopBucket.bucket) {
            console.error('[breakchop] bucket not initialized');
            return;
          }
          const dbItems = await breakchopBucket.dbGetAll(breakchopBucket.bucket.bucketId);
          const item = dbItems.find((i) => i.id === itemId);
          if (!item) {
            console.error('[breakchop] bucket item not found:', itemId, 'in bucket', breakchopBucket.bucket.bucketId, '— dbItems:', dbItems.length);
            // Try in-memory items as fallback
            const memItem = breakchopBucket.bucket.items.find((i) => i.id === itemId);
            if (memItem) {
              await loadSampleFromBucketItem(memItem);
            }
            return;
          }
          await loadSampleFromBucketItem(item);
        } catch (err) {
          console.error('[breakchop] Bucket drop error:', err);
        }
        return;
      }

      // OS file drop
      const file = e.dataTransfer.files[0];
      if (file && (file.type.startsWith('audio/') || file.name.match(/\.(wav|mp3|ogg|flac|aac|m4a)$/i))) {
        loadAudioFile(file);
      }
    });
  });
}

async function loadAudioFile(file) {
  store.fileName = file.name.replace(/\.[^/.]+$/, '');
  const arrayBuffer = await file.arrayBuffer();
  const ctx = getAudioContext();
  if (ctx.state === 'suspended') await ctx.resume();

  // Try manual WAV parse first (handles 24-bit correctly), fallback to decodeAudioData
  let audioBuffer = null;
  if (file.name.toLowerCase().endsWith('.wav')) {
    audioBuffer = parseWavToAudioBuffer(arrayBuffer, ctx);
  }
  if (!audioBuffer) {
    audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
  }
  store.audioBuffer = audioBuffer;

  // Show waveform, hide dropzone
  waveformDropzone.style.display = 'none';
  waveformContainer.style.display = 'block';

  // Reset slices
  store.slices = [];
  store.pads = [];

  // Wait for layout to settle before drawing
  requestAnimationFrame(() => {
    drawWaveform();
    renderSliceMarkers();
    updateSliceCount();
    renderPads();
    renderSequencer();
    // Auto-detect transients on load
    detectTransients();
  });
}

// ============================================================================
// WAVEFORM RENDERING (reuses renderWaveform from shared/ui)
// ============================================================================

async function drawWaveform() {
  if (!store.audioBuffer) return;

  const canvas = waveformCanvas;
  const container = waveformContainer;

  // Wait for layout to settle (container must be visible with real dimensions)
  let drawWidth = container.clientWidth;
  let drawHeight = container.clientHeight;
  if (drawWidth < 2 || drawHeight < 2) {
    requestAnimationFrame(() => drawWaveform());
    return;
  }

  canvas.width = drawWidth;
  canvas.height = drawHeight;

  const totalSamples = store.audioBuffer.length;
  if (totalSamples === 0) return;

  const startSample = Math.floor(store.viewStart * totalSamples);
  const endSample = Math.floor(store.viewEnd * totalSamples);
  if (endSample <= startSample) return;

  const channelCount = store.audioBuffer.numberOfChannels;
  const isStereo = channelCount > 1;
  const halfHeight = Math.floor(drawHeight / 2);
  const renderHeight = isStereo ? halfHeight : drawHeight;

  const [lcanv, rcanv] = await Promise.all([
    renderWaveform(store.audioBuffer, { channel: 0, startSample, endSample }, drawWidth, renderHeight, 1),
    isStereo
      ? renderWaveform(store.audioBuffer, { channel: 1, startSample, endSample }, drawWidth, renderHeight, 1)
      : Promise.resolve(null),
  ]);

  const ctx = canvas.getContext('2d');
  ctx.fillStyle = window.themeColors ? window.themeColors[0] : '#000';
  ctx.fillRect(0, 0, drawWidth, drawHeight);

  ctx.drawImage(lcanv, 0, 0);

  if (isStereo && rcanv) {
    ctx.drawImage(rcanv, 0, halfHeight);
    ctx.strokeStyle = window.themeColors ? window.themeColors[4] : '#3c3c3c';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0, halfHeight);
    ctx.lineTo(drawWidth, halfHeight);
    ctx.stroke();
  }

  renderSliceMarkers();
}

function renderSliceMarkers() {
  if (!store.audioBuffer) return;
  sliceMarkers.innerHTML = '';

  const totalSamples = store.audioBuffer.length;
  const visibleStart = store.viewStart * totalSamples;
  const visibleEnd = store.viewEnd * totalSamples;
  const visibleRange = visibleEnd - visibleStart;
  const containerWidth = waveformContainer.clientWidth;

  store.slices.forEach((slice, i) => {
    const marker = build('div');
    marker.className = 'slice-marker';
    const pct = ((slice.start - visibleStart) / visibleRange) * 100;
    if (pct < 0 || pct > 100) return;
    marker.style.left = pct + '%';
    marker.dataset.sliceIndex = i;
    marker.title = `Slice ${i + 1}`;

    // Click to play this slice
    marker.addEventListener('click', (e) => {
      e.stopPropagation();
      playSlice(i);
    });

    // Right-click to delete this slice
    marker.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      e.stopPropagation();
      store.slices.splice(i, 1);
      // Renumber remaining slices
      store.slices.forEach((s, idx) => (s.name = `slice_${idx + 1}`));
      autoAssignPads();
      renderSliceMarkers();
      updateSliceCount();
      renderPads();
      renderSequencer();
    });

    // Drag to move slice point
    let dragging = false;
    marker.addEventListener('mousedown', (e) => {
      e.preventDefault();
      e.stopPropagation();
      dragging = true;
    });
    document.addEventListener('mousemove', (e) => {
      if (!dragging) return;
      const rect = waveformContainer.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const samplePos = visibleStart + (x / rect.width) * visibleRange;
      store.slices[i].start = Math.max(0, Math.min(totalSamples - 1, Math.floor(samplePos)));
      renderSliceMarkers();
    });
    document.addEventListener('mouseup', () => {
      if (dragging) dragging = false;
    });

    sliceMarkers.appendChild(marker);
  });
}

// ============================================================================
// TRANSIENT DETECTION & SLICING
// ============================================================================

function detectTransients() {
  if (!store.audioBuffer) return;

  const sensitivity = parseFloat(sensitivitySlider.value);
  const minSliceMs = parseInt(minSliceSlider.value);
  const sampleRate = store.audioBuffer.sampleRate;
  const minSliceSamples = (minSliceMs / 1000) * sampleRate;
  const totalSamples = store.audioBuffer.length;
  const numChannels = store.audioBuffer.numberOfChannels;

  // Mix down to mono
  const mono = new Float32Array(totalSamples);
  for (let ch = 0; ch < numChannels; ch++) {
    const chData = store.audioBuffer.getChannelData(ch);
    for (let i = 0; i < totalSamples; i++) {
      mono[i] += chData[i] / numChannels;
    }
  }

  // ---- Stage 1: Envelope follower (analog-style) ----
  // Fast attack tracks percussive onsets precisely.
  // Medium release lets the envelope decay between hits but not within a hit.
  // This is the same technique used by hardware drum triggers.
  const attackTC = sampleRate * 0.0003;   // 0.3ms attack time constant
  const releaseTC = sampleRate * 0.015;    // 15ms release time constant
  const attackCoeff = Math.exp(-1 / attackTC);
  const releaseCoeff = Math.exp(-1 / releaseTC);

  const envelope = new Float32Array(totalSamples);
  let env = 0;
  for (let i = 0; i < totalSamples; i++) {
    const x = Math.abs(mono[i]);
    if (x > env) {
      env = attackCoeff * env + (1 - attackCoeff) * x;
    } else {
      env = releaseCoeff * env + (1 - releaseCoeff) * x;
    }
    envelope[i] = env;
  }

  // ---- Stage 2: Log compression ----
  // Compress dynamic range so quiet hits (ghost notes, hats) are detected
  // alongside loud hits (kicks, snares). Without this, only the loudest
  // hits trigger and ghost notes are missed.
  const logEnv = new Float32Array(totalSamples);
  const logScale = 1000;
  const logBase = Math.log(1 + logScale);
  for (let i = 0; i < totalSamples; i++) {
    logEnv[i] = Math.log(1 + envelope[i] * logScale) / logBase;
  }

  // ---- Stage 3: Derivative (onset detection function) ----
  // The rate-of-change of the log envelope spikes at attack onsets.
  // We sample at hop=32 for sample-accurate onset positioning.
  const hop = 32;
  const numFrames = Math.floor(totalSamples / hop);
  const odf = new Float32Array(numFrames);
  for (let f = 1; f < numFrames; f++) {
    const prev = logEnv[(f - 1) * hop];
    const curr = logEnv[f * hop];
    // Half-wave rectified derivative — only positive changes (attacks)
    odf[f] = Math.max(0, curr - prev);
  }

  // ---- Stage 4: Smooth ODF with small moving average ----
  // Removes sample-level jitter while preserving onset sharpness.
  // 3-frame window (~2ms) is enough to smooth without blurring.
  const smoothOdf = new Float32Array(numFrames);
  for (let f = 0; f < numFrames; f++) {
    let sum = 0, count = 0;
    for (let k = -1; k <= 1; k++) {
      const idx = f + k;
      if (idx >= 0 && idx < numFrames) { sum += odf[idx]; count++; }
    }
    smoothOdf[f] = sum / count;
  }

  // ---- Stage 5: Find global max for normalization ----
  let maxOdf = 0;
  for (let f = 0; f < numFrames; f++) {
    if (smoothOdf[f] > maxOdf) maxOdf = smoothOdf[f];
  }
  if (maxOdf <= 0) return;

  // Normalize to 0..1
  for (let f = 0; f < numFrames; f++) smoothOdf[f] /= maxOdf;

  // ---- Stage 6: Peak picking ----
  // Threshold is a fraction of the global max, controlled by sensitivity.
  // sensitivity=1 → threshold=0.15 (detects most hits)
  // sensitivity=3 → threshold=0.05 (detects everything including ghost notes)
  // sensitivity=0.2 → threshold=0.5 (only the loudest hits)
  const threshold = 0.15 / sensitivity;

  const peaks = [];
  let lastPeakSample = -minSliceSamples;

  for (let f = 2; f < numFrames - 2; f++) {
    if (smoothOdf[f] < threshold) continue;
    // Local maximum (strict)
    if (smoothOdf[f] <= smoothOdf[f - 1] || smoothOdf[f] < smoothOdf[f + 1]) continue;

    const samplePos = f * hop;
    if (samplePos - lastPeakSample < minSliceSamples) continue;

    // ---- Stage 7: Refine onset to actual attack point ----
    // Walk backwards from the peak to find where the envelope
    // starts rising — the true hit point. We look for the minimum
    // envelope value in the lookback window (the valley before the attack).
    const lookback = Math.floor(sampleRate * 0.02 / hop); // 20ms lookback
    let onsetFrame = f;
    let minEnv = logEnv[f * hop];
    for (let b = 1; b <= lookback && f - b > 0; b++) {
      const e = logEnv[(f - b) * hop];
      if (e <= minEnv) {
        minEnv = e;
        onsetFrame = f - b;
      }
      // If we've found a clear valley and energy starts rising, stop
      if (e > minEnv * 1.05 && b > 2) break;
    }

    const onsetSample = onsetFrame * hop;
    if (onsetSample - lastPeakSample < minSliceSamples) continue;

    peaks.push(onsetSample);
    lastPeakSample = onsetSample;
  }

  // Always add start of file
  if (peaks.length === 0 || peaks[0] > minSliceSamples) {
    peaks.unshift(0);
  }

  // Create slices from peaks
  store.slices = [];
  for (let i = 0; i < peaks.length; i++) {
    const start = peaks[i];
    const end = i < peaks.length - 1 ? peaks[i + 1] : totalSamples;
    store.slices.push({
      start,
      end,
      name: `slice_${i + 1}`,
    });
  }

  // Auto-assign slices to pads (one pad per slice)
  autoAssignPads();

  renderSliceMarkers();
  updateSliceCount();
  renderPads();
  renderSequencer();
}

function autoAssignPads() {
  const numSlices = store.slices.length;
  // Grow or shrink the pads array to match the slice count
  while (store.pads.length < numSlices) store.pads.push(makePad());
  if (store.pads.length > numSlices) store.pads.length = numSlices;
  for (let i = 0; i < store.pads.length; i++) {
    store.pads[i].sliceIndex = i;
  }
}

function clearSlices() {
  store.slices = [];
  store.pads = [];
  renderSliceMarkers();
  updateSliceCount();
  renderPads();
  renderSequencer();
}

function updateSliceCount() {
  sliceCountEl.textContent = store.slices.length;
}

// ============================================================================
// SLICE PLAYBACK
// ============================================================================

function playSlice(sliceIndex, padIndex) {
  if (!store.audioBuffer || sliceIndex < 0 || sliceIndex >= store.slices.length) return;

  const slice = store.slices[sliceIndex];
  const ctx = getAudioContext();
  const pad = padIndex !== undefined ? store.pads[padIndex] : null;

  const length = slice.end - slice.start;
  if (length <= 0) return;

  // Create a buffer for this slice
  const numChannels = store.audioBuffer.numberOfChannels;
  const sliceBuffer = ctx.createBuffer(numChannels, length, store.audioBuffer.sampleRate);

  for (let ch = 0; ch < numChannels; ch++) {
    const srcData = store.audioBuffer.getChannelData(ch);
    const dstData = sliceBuffer.getChannelData(ch);
    if (pad && pad.reverse) {
      for (let i = 0; i < length; i++) {
        dstData[i] = srcData[slice.start + length - 1 - i];
      }
    } else {
      dstData.set(srcData.subarray(slice.start, slice.end));
    }
  }

  // Create source node
  const source = ctx.createBufferSource();
  source.buffer = sliceBuffer;

  // Pitch shift via playbackRate
  if (pad && pad.pitch !== 0) {
    source.playbackRate.value = Math.pow(2, pad.pitch / 12);
  }

  // Gain (volume + decay)
  const gainNode = ctx.createGain();
  if (pad) {
    gainNode.gain.value = pad.vol;
    // Decay envelope
    const decayTime = (length / store.audioBuffer.sampleRate) * pad.decay;
    gainNode.gain.setValueAtTime(pad.vol, ctx.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + decayTime);
  } else {
    gainNode.gain.value = 1.0;
  }

  // Panner
  let pannerNode = null;
  if (pad && pad.pan !== 0) {
    pannerNode = ctx.createStereoPanner();
    pannerNode.pan.value = pad.pan;
  }

  // Connect chain
  source.connect(gainNode);
  if (pannerNode) {
    gainNode.connect(pannerNode);
    pannerNode.connect(ctx.destination);
  } else {
    gainNode.connect(ctx.destination);
  }

  source.start();

  // Visual feedback
  if (padIndex !== undefined) {
    const padEl = qs(`.pad[data-pad="${padIndex}"]`);
    if (padEl) {
      padEl.classList.add('pad-playing');
      setTimeout(() => padEl.classList.remove('pad-playing'), 200);
    }
  }
}

function playPad(padIndex) {
  const pad = store.pads[padIndex];
  if (!pad || pad.sliceIndex < 0) return;
  playSlice(pad.sliceIndex, padIndex);
}

// ============================================================================
// PADS UI
// ============================================================================

function renderPads() {
  padsGrid.innerHTML = '';
  for (let i = 0; i < store.pads.length; i++) {
    const pad = store.pads[i];
    const el = build('div');
    el.className = 'pad';
    if (pad.sliceIndex < 0) el.classList.add('pad-empty');
    el.dataset.pad = i;

    const num = build('div');
    num.className = 'pad-num';
    num.textContent = i + 1;
    el.appendChild(num);

    if (pad.sliceIndex >= 0) {
      const name = build('div');
      name.className = 'pad-name';
      name.textContent = store.slices[pad.sliceIndex]?.name || `S${pad.sliceIndex + 1}`;
      el.appendChild(name);

      // Mini waveform
      drawPadWaveform(el, pad.sliceIndex);
    }

    // Click to play
    el.addEventListener('click', () => {
      playPad(i);
    });

    // Right-click for params
    el.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      openPadParams(i);
    });

    padsGrid.appendChild(el);
  }
}

async function drawPadWaveform(padEl, sliceIndex) {
  if (!store.audioBuffer || sliceIndex < 0) return;
  const slice = store.slices[sliceIndex];
  if (!slice) return;

  const canvas = build('canvas');
  canvas.className = 'pad-waveform';
  canvas.width = 60;
  canvas.height = 24;

  const numChannels = store.audioBuffer.numberOfChannels;
  const cnv = await renderWaveform(
    store.audioBuffer,
    { channel: 0, startSample: slice.start, endSample: slice.end },
    60,
    24,
    1
  );
  const ctx = canvas.getContext('2d');
  ctx.drawImage(cnv, 0, 0);

  padEl.appendChild(canvas);
}

// ============================================================================
// PAD PARAMETERS MODAL
// ============================================================================

function openPadParams(padIndex) {
  const pad = store.pads[padIndex];
  if (!pad) return;
  store.selectedPad = padIndex;

  const modal = qs('#pad-params-modal');
  const title = qs('#ppm-title');
  const pitchEl = qs('#ppm-pitch');
  const pitchVal = qs('#ppm-pitch-val');
  const reverseEl = qs('#ppm-reverse');
  const decayEl = qs('#ppm-decay');
  const decayVal = qs('#ppm-decay-val');
  const panEl = qs('#ppm-pan');
  const panVal = qs('#ppm-pan-val');
  const volEl = qs('#ppm-vol');
  const volVal = qs('#ppm-vol-val');

  title.textContent = `Pad ${padIndex + 1}`;
  pitchEl.value = pad.pitch;
  pitchVal.textContent = pad.pitch;
  reverseEl.checked = pad.reverse;
  decayEl.value = pad.decay;
  decayVal.textContent = pad.decay.toFixed(2);
  panEl.value = pad.pan;
  panVal.textContent = pad.pan.toFixed(2);
  volEl.value = pad.vol;
  volVal.textContent = pad.vol.toFixed(2);

  modal.removeAttribute('hidden');

  // Live update handlers
  const onPitch = () => { pad.pitch = parseInt(pitchEl.value); pitchVal.textContent = pad.pitch; };
  const onReverse = () => { pad.reverse = reverseEl.checked; };
  const onDecay = () => { pad.decay = parseFloat(decayEl.value); decayVal.textContent = pad.decay.toFixed(2); };
  const onPan = () => { pad.pan = parseFloat(panEl.value); panVal.textContent = pad.pan.toFixed(2); };
  const onVol = () => { pad.vol = parseFloat(volEl.value); volVal.textContent = pad.vol.toFixed(2); };

  pitchEl.oninput = onPitch;
  reverseEl.onchange = onReverse;
  decayEl.oninput = onDecay;
  panEl.oninput = onPan;
  volEl.oninput = onVol;
}

function closePadParams() {
  qs('#pad-params-modal').setAttribute('hidden', '');
}

// ============================================================================
// STEP SEQUENCER
// ============================================================================

function renderSequencer() {
  sequencerGrid.innerHTML = '';
  const steps = store.numSteps;

  // Header row (step numbers)
  const headerRow = build('div');
  headerRow.className = 'seq-header-row';
  headerRow.style.gridTemplateColumns = `28px repeat(${steps}, 1fr)`;

  const labelCell = build('div');
  labelCell.className = 'seq-row-label';
  labelCell.textContent = '';
  headerRow.appendChild(labelCell);

  for (let s = 0; s < steps; s++) {
    const cell = build('div');
    cell.className = 'seq-header-cell';
    if (s % 4 === 0) cell.classList.add('seq-beat-start');
    cell.textContent = s + 1;
    headerRow.appendChild(cell);
  }
  sequencerGrid.appendChild(headerRow);

  // One row per pad (only show pads with slices assigned)
  for (let p = 0; p < store.pads.length; p++) {
    const pad = store.pads[p];
    if (pad.sliceIndex < 0) continue;

    const row = build('div');
    row.className = 'seq-row';
    row.style.gridTemplateColumns = `28px repeat(${steps}, 1fr)`;

    const label = build('div');
    label.className = 'seq-row-label';
    label.textContent = `P${p + 1}`;
    label.title = store.slices[pad.sliceIndex]?.name || '';
    row.appendChild(label);

    for (let s = 0; s < steps; s++) {
      const cell = build('div');
      cell.className = 'seq-cell';
      if (s % 4 === 0) cell.classList.add('seq-beat-start');
      cell.dataset.step = s;
      cell.dataset.pad = p;

      if (store.pattern[s] && store.pattern[s].includes(p)) {
        cell.classList.add('seq-cell-active');
      }

      // Show sub-step indicator
      const subs = store.subSteps[`${s}:${p}`];
      if (subs && subs > 0) {
        cell.classList.add('seq-cell-drill');
        cell.textContent = subs;
      }

      cell.addEventListener('click', () => {
        toggleStep(s, p);
      });

      // Right-click cycles through drill subdivisions: 0→2→3→4→6→8→0
      cell.addEventListener('contextmenu', (e) => {
        e.preventDefault();
        e.stopPropagation();
        cycleSubSteps(s, p);
      });

      row.appendChild(cell);
    }
    sequencerGrid.appendChild(row);
  }
}

function toggleStep(step, padIndex) {
  if (!store.pattern[step]) store.pattern[step] = [];
  const idx = store.pattern[step].indexOf(padIndex);
  if (idx >= 0) {
    store.pattern[step].splice(idx, 1);
    // Clear sub-steps when removing the step
    delete store.subSteps[`${step}:${padIndex}`];
  } else {
    store.pattern[step].push(padIndex);
  }
  renderSequencer();
}

// Drill subdivision cycle: 0 (none) → 2 → 3 → 4 → 6 → 8 → 0
const DRILL_CYCLE = [0, 2, 3, 4, 6, 8];

function cycleSubSteps(step, padIndex) {
  // Step must be active to add drill
  if (!store.pattern[step] || !store.pattern[step].includes(padIndex)) {
    // Auto-activate the step first
    store.pattern[step] = store.pattern[step] || [];
    store.pattern[step].push(padIndex);
  }

  const key = `${step}:${padIndex}`;
  const current = store.subSteps[key] || 0;
  const currentIdx = DRILL_CYCLE.indexOf(current);
  const nextIdx = (currentIdx + 1) % DRILL_CYCLE.length;
  const nextVal = DRILL_CYCLE[nextIdx];

  if (nextVal === 0) {
    delete store.subSteps[key];
  } else {
    store.subSteps[key] = nextVal;
  }
  renderSequencer();
}

function clearPattern() {
  for (let i = 0; i < store.numSteps; i++) store.pattern[i] = [];
  store.subSteps = {};
  renderSequencer();
}

function randomizePattern() {
  clearPattern();

  const fillPct = parseInt(fillSlider.value);   // 0-100: density of fills
  const chaosPct = parseInt(chaosSlider.value);  // 0-100: 0=smart, 100=chaos
  const steps = store.numSteps;
  const activePads = [];
  for (let p = 0; p < store.pads.length; p++) {
    if (store.pads[p].sliceIndex >= 0) activePads.push(p);
  }
  if (activePads.length === 0) { renderSequencer(); return; }

  // fillProb: probability any given step gets a hit
  // 0% → 0 hits, 100% → every step filled
  const fillProb = fillPct / 100;

  // chaosFactor: 0 = fully smart, 1 = fully random
  const chaos = chaosPct / 100;

  // ---- Smart pattern knowledge ----
  // At chaos=0, generate plausible drum patterns:
  // - Kick on beats (steps 0, 4, 8, 12 for 16-step)
  // - Snare on backbeats (steps 4, 12 for 16-step)
  // - Hats on offbeats / every 2 steps
  // - Ghost notes sparingly
  // As chaos increases, randomness takes over.

  const beatLen = steps / 4; // steps per beat (4, 8, 16 → 1, 2, 4)

  // Classify pads by slice length — shorter slices = hats/ghosts, longer = kick/snare
  const padInfo = activePads.map(p => {
    const slice = store.slices[store.pads[p].sliceIndex];
    const lenSamples = slice ? slice.end - slice.start : 0;
    const lenMs = lenSamples / store.audioBuffer.sampleRate * 1000;
    return { pad: p, lenMs };
  });

  // Sort by length: longest = kick, medium = snare, shortest = hats
  const sorted = [...padInfo].sort((a, b) => b.lenMs - a.lenMs);
  const kickPad = sorted[0]?.pad;
  const snarePad = sorted[Math.floor(sorted.length * 0.4)]?.pad;
  const hatPads = sorted.slice(Math.floor(sorted.length * 0.5)).map(s => s.pad);

  for (let s = 0; s < steps; s++) {
    const isBeat = s % beatLen === 0;
    const isBackbeat = (s % (beatLen * 2) === beatLen); // steps 4, 12 for 16-step
    const isOffbeat = s % (beatLen / 2) === (beatLen / 2) || (beatLen < 2 && s % 2 === 1);

    // Smart probabilities (chaos=0)
    let kickProb = 0, snareProb = 0, hatProb = 0, ghostProb = 0;

    if (isBeat) kickProb = 0.85;
    if (isBackbeat) snareProb = 0.9;
    if (isOffbeat || isBeat) hatProb = 0.6;
    ghostProb = 0.08; // sparse ghost notes

    // Scale by fill density
    kickProb *= fillProb;
    snareProb *= fillProb;
    hatProb *= fillProb;
    ghostProb *= fillProb;

    // Mix smart with chaos
    const randomProb = fillProb * 0.5;

    // Kick
    if (kickPad !== undefined) {
      const prob = kickProb * (1 - chaos) + randomProb * chaos;
      if (Math.random() < prob) store.pattern[s].push(kickPad);
    }

    // Snare
    if (snarePad !== undefined && snarePad !== kickPad) {
      const prob = snareProb * (1 - chaos) + randomProb * chaos;
      if (Math.random() < prob) store.pattern[s].push(snarePad);
    }

    // Hats / other pads
    for (const hp of hatPads) {
      if (hp === kickPad || hp === snarePad) continue;
      const isHat = hatPads.includes(hp);
      const baseProb = isHat ? hatProb : ghostProb;
      const prob = baseProb * (1 - chaos) + randomProb * chaos * 0.7;
      if (Math.random() < prob) {
        store.pattern[s].push(hp);
        maybeAddDrill(s, hp, chaos, fillProb);
      }
    }

    // Also maybe add drill to kick/snare
    if (store.pattern[s].includes(kickPad)) maybeAddDrill(s, kickPad, chaos, fillProb);
    if (store.pattern[s].includes(snarePad)) maybeAddDrill(s, snarePad, chaos, fillProb);
  }

  renderSequencer();
}

// Randomly add drill subdivisions to a step
// Chaos increases drill probability and favors faster subdivisions
function maybeAddDrill(step, pad, chaos, fillProb) {
  // Base drill chance: low at chaos=0 (~5%), high at chaos=1 (~35%)
  const drillChance = (0.05 + chaos * 0.30) * fillProb;
  if (Math.random() > drillChance) return;

  // Pick a subdivision — chaos favors faster rolls
  const drillOptions = [2, 3, 4, 6, 8];
  let weights;
  if (chaos < 0.3) {
    // Smart: mostly 2s and 3s, occasional 4
    weights = [5, 3, 1, 0.5, 0.1];
  } else if (chaos < 0.7) {
    // Medium: spread across 2-4
    weights = [3, 2, 2, 1, 0.5];
  } else {
    // Chaos: full range, favor fast
    weights = [1, 1, 2, 2, 2];
  }

  const totalWeight = weights.reduce((a, b) => a + b, 0);
  let r = Math.random() * totalWeight;
  let drillVal = 2;
  for (let i = 0; i < drillOptions.length; i++) {
    r -= weights[i];
    if (r <= 0) { drillVal = drillOptions[i]; break; }
  }

  store.subSteps[`${step}:${pad}`] = drillVal;
}

// ---- Sequencer playback (lookahead scheduler) ----

function startSequencer() {
  if (store.isPlaying) return;
  const ctx = getAudioContext();
  if (ctx.state === 'suspended') ctx.resume();
  store.isPlaying = true;
  store.currentStep = 0;
  store.nextStepTime = ctx.currentTime + 0.1;
  scheduler();
}

function stopSequencer() {
  store.isPlaying = false;
  if (store.schedulerId) {
    clearTimeout(store.schedulerId);
    store.schedulerId = null;
  }
  // Clear playing highlight
  qsa('.seq-cell-playing').forEach((el) => el.classList.remove('seq-cell-playing'));
  qsa('.pad-playing').forEach((el) => el.classList.remove('pad-playing'));
}

function scheduler() {
  if (!store.isPlaying) return;
  const ctx = getAudioContext();
  const stepDuration = 60 / store.bpm / 4; // 16th notes

  while (store.nextStepTime < ctx.currentTime + 0.1) {
    const step = store.currentStep;
    const padsToPlay = store.pattern[step] || [];

    // Schedule playback — check for sub-step drills
    padsToPlay.forEach((padIndex) => {
      const pad = store.pads[padIndex];
      if (!pad || pad.sliceIndex < 0) return;

      const subs = store.subSteps[`${step}:${padIndex}`];
      if (subs && subs > 1) {
        // Drill: play `subs` times evenly spaced within the step duration
        const subDuration = stepDuration / subs;
        for (let i = 0; i < subs; i++) {
          playSliceAtTime(pad.sliceIndex, padIndex, store.nextStepTime + i * subDuration);
        }
      } else {
        playSliceAtTime(pad.sliceIndex, padIndex, store.nextStepTime);
      }
    });

    // Visual highlight (schedule via setTimeout)
    const visualDelay = Math.max(0, (store.nextStepTime - ctx.currentTime) * 1000);
    setTimeout(() => {
      qsa('.seq-cell-playing').forEach((el) => el.classList.remove('seq-cell-playing'));
      qsa('.pad-playing').forEach((el) => el.classList.remove('pad-playing'));
      // Highlight current step column
      qsa(`.seq-cell[data-step="${step}"]`).forEach((el) => el.classList.add('seq-cell-playing'));
      padsToPlay.forEach((padIndex) => {
        const padEl = qs(`.pad[data-pad="${padIndex}"]`);
        if (padEl) {
          padEl.classList.add('pad-playing');
          setTimeout(() => padEl.classList.remove('pad-playing'), 150);
        }
      });
    }, visualDelay);

    // Advance
    store.nextStepTime += stepDuration;
    store.currentStep++;
    if (store.currentStep >= store.numSteps) {
      if (store.loop) {
        store.currentStep = 0;
      } else {
        stopSequencer();
        return;
      }
    }
  }

  store.schedulerId = setTimeout(scheduler, 25);
}

function playSliceAtTime(sliceIndex, padIndex, when) {
  if (!store.audioBuffer || sliceIndex < 0 || sliceIndex >= store.slices.length) return;

  const slice = store.slices[sliceIndex];
  const ctx = getAudioContext();
  const pad = store.pads[padIndex];
  const length = slice.end - slice.start;
  if (length <= 0) return;

  const numChannels = store.audioBuffer.numberOfChannels;
  const sliceBuffer = ctx.createBuffer(numChannels, length, store.audioBuffer.sampleRate);

  for (let ch = 0; ch < numChannels; ch++) {
    const srcData = store.audioBuffer.getChannelData(ch);
    const dstData = sliceBuffer.getChannelData(ch);
    if (pad && pad.reverse) {
      for (let i = 0; i < length; i++) {
        dstData[i] = srcData[slice.start + length - 1 - i];
      }
    } else {
      dstData.set(srcData.subarray(slice.start, slice.end));
    }
  }

  const source = ctx.createBufferSource();
  source.buffer = sliceBuffer;

  if (pad && pad.pitch !== 0) {
    source.playbackRate.value = Math.pow(2, pad.pitch / 12);
  }

  const gainNode = ctx.createGain();
  if (pad) {
    gainNode.gain.setValueAtTime(pad.vol, when);
    const decayTime = (length / store.audioBuffer.sampleRate) * pad.decay;
    gainNode.gain.exponentialRampToValueAtTime(0.001, when + decayTime);
  }

  let pannerNode = null;
  if (pad && pad.pan !== 0) {
    pannerNode = ctx.createStereoPanner();
    pannerNode.pan.value = pad.pan;
  }

  source.connect(gainNode);
  if (pannerNode) {
    gainNode.connect(pannerNode);
    pannerNode.connect(ctx.destination);
  } else {
    gainNode.connect(ctx.destination);
  }

  source.start(when);
}

// ============================================================================
// SAVE MODAL — dispatch
// ============================================================================

function handleSaveAction(action) {
  switch (action) {
    case 'chops-to-bucket':   sendSlicesToBucket(); break;
    case 'chops-zip':         downloadChopsAsZip(); break;
    case 'seq-to-bucket':     exportWavMix(); break;
    case 'seq-download':      downloadSequencerLoop(); break;
    case 'rx2':               exportRx2(); break;
  }
}

// ============================================================================
// EXPORT — WAV mix (sequencer loop → bucket)
// ============================================================================

function exportWavMix() {
  if (!store.audioBuffer) {
    alertmessage('Load an audio file first');
    return;
  }

  const ctx = getAudioContext();
  const stepDuration = 60 / store.bpm / 4;
  const totalSteps = store.numSteps;
  const totalDuration = stepDuration * totalSteps;
  const sampleRate = store.audioBuffer.sampleRate;
  const numChannels = store.audioBuffer.numberOfChannels;
  const totalSamples = Math.ceil(totalDuration * sampleRate);

  // Create offline context
  const offlineCtx = new OfflineAudioContext(numChannels, totalSamples, sampleRate);

  // Render each step
  for (let step = 0; step < totalSteps; step++) {
    const when = step * stepDuration;
    const padsToPlay = store.pattern[step] || [];
    padsToPlay.forEach((padIndex) => {
      const pad = store.pads[padIndex];
      if (!pad || pad.sliceIndex < 0) return;
      const slice = store.slices[pad.sliceIndex];
      if (!slice) return;
      const length = slice.end - slice.start;
      if (length <= 0) return;

      // Check for sub-step drill
      const subs = store.subSteps[`${step}:${padIndex}`];
      const playTimes = (subs && subs > 1)
        ? Array.from({ length: subs }, (_, i) => when + i * (stepDuration / subs))
        : [when];

      playTimes.forEach((playWhen) => {
        const sliceBuffer = offlineCtx.createBuffer(numChannels, length, sampleRate);
        for (let ch = 0; ch < numChannels; ch++) {
          const srcData = store.audioBuffer.getChannelData(ch);
          const dstData = sliceBuffer.getChannelData(ch);
          if (pad.reverse) {
            for (let i = 0; i < length; i++) {
              dstData[i] = srcData[slice.start + length - 1 - i];
            }
          } else {
            dstData.set(srcData.subarray(slice.start, slice.end));
          }
        }

        const source = offlineCtx.createBufferSource();
        source.buffer = sliceBuffer;
        if (pad.pitch !== 0) source.playbackRate.value = Math.pow(2, pad.pitch / 12);

        const gainNode = offlineCtx.createGain();
        gainNode.gain.setValueAtTime(pad.vol, playWhen);
        const decayTime = (length / sampleRate) * pad.decay;
        gainNode.gain.exponentialRampToValueAtTime(0.001, playWhen + decayTime);

        let pannerNode = null;
        if (pad.pan !== 0) {
          pannerNode = offlineCtx.createStereoPanner();
          pannerNode.pan.value = pad.pan;
        }

        source.connect(gainNode);
        if (pannerNode) {
          gainNode.connect(pannerNode);
          pannerNode.connect(offlineCtx.destination);
        } else {
          gainNode.connect(offlineCtx.destination);
        }
        source.start(playWhen);
      });
    });
  }

  offlineCtx.startRendering().then((renderedBuffer) => {
    const wav = audioBufferToWav(renderedBuffer);
    const sampleRate = renderedBuffer.sampleRate;
    const numChannels = renderedBuffer.numberOfChannels;

    if (breakchopBucket.bucket) {
      breakchopBucket.bucket.add({
        source: 'breakchop',
        name: `${store.fileName || 'breakchop'}_mix.wav`,
        wavLike: new Uint8Array(wav),
        sampleRate,
        channels: numChannels,
        bitDepth: 16,
      });
    } else {
      // Fallback: download if bucket not available
      const blob = new Blob([wav], { type: 'audio/wav' });
      const url = URL.createObjectURL(blob);
      const a = build('a');
      a.href = url;
      a.download = `${store.fileName || 'breakchop'}_mix.wav`;
      a.click();
      URL.revokeObjectURL(url);
    }
  });
}

// ============================================================================
// EXPORT — REX2 (DWOP-compressed, ReCycle-compatible structure)
// ============================================================================
// REX2 file structure (big-endian IFF), matching real ReCycle files:
//   CAT <size> 'REX2'
//     HEAD <size> <magic> <version> <zeros> <filename>
//     GLOB <size> <ppq> <timeSig> <beats> ... <tempo> <flags>
//     RECY <size> <loopData>
//     CAT <size> 'DEVL'
//       TRSH <size> <threshold>
//       EQ   <size> <eqSettings>
//       COMP <size> <compSettings>
//     CAT <size> 'SLCL'
//       SLCE <size> <marker> <length> <gain> <flags>  (one per slice)
//     SINF <size> <channels> <depth> <rate> <frames> <loopStart> <loopEnd>
//     SDAT <size> <DWOP-compressed audio>
//
// Uses the DWOP encoder (inverse of the REX Shared Library decoder) to
// produce real, compressed REX2 files that are bit-compatible with ReCycle.

function exportRx2() {
  if (!store.audioBuffer || store.slices.length === 0) {
    alertmessage('Load audio and create slices first');
    return;
  }

  const { encodeMono, encodeStereo } = require('../../../../shared/src/common/rex2-dwop.cjs');

  const sampleRate = store.audioBuffer.sampleRate;
  const numChannels = store.audioBuffer.numberOfChannels;
  const frameCount = store.audioBuffer.length;
  const tempo = Math.round(store.bpm * 1000);
  const fileName = (store.fileName || 'breakchop') + '.rx2';

  // Convert Float32 audio to Int32 PCM (16-bit range, doubled domain)
  const pcm = [];
  for (let ch = 0; ch < numChannels; ch++) {
    const floatData = store.audioBuffer.getChannelData(ch);
    const intData = new Int32Array(frameCount);
    for (let i = 0; i < frameCount; i++) {
      const s = Math.max(-1, Math.min(1, floatData[i]));
      intData[i] = Math.round(s * 0x7fff);
    }
    pcm.push(intData);
  }

  // DWOP-encode the audio
  let sdatBytes;
  if (numChannels === 1) {
    sdatBytes = encodeMono(pcm[0]);
  } else {
    sdatBytes = encodeStereo(pcm[0], pcm[1]);
  }

  // Build top-level chunks (all big-endian)
  const chunks = [];

  // HEAD chunk — magic + version + filename
  // Structure (from real REX2 files):
  //   bytes 0-3:   magic = 0x490cf18d
  //   bytes 4-7:   version = 0xbc020000 (ReCycle format version)
  //   bytes 8-27:  zeros (20 bytes)
  //   byte  28:    filename length (1 byte)
  //   bytes 29+:   filename string (no null terminator)
  // HEAD size = 29 + nameLen (39 for 10-char name, 40 for 11-char name)
  const shortName = fileName.slice(0, 11); // max 11 chars
  const headSize = 29 + shortName.length;
  const headData = new ArrayBuffer(headSize);
  const headView = new DataView(headData);
  headView.setUint32(0, 0x490cf18d, false);    // magic
  headView.setUint32(4, 0xbc020000, false);    // version (ReCycle format)
  // bytes 8-27: zeros (already 0)
  headView.setUint8(28, shortName.length);     // filename length
  for (let i = 0; i < shortName.length; i++) {
    headView.setUint8(29 + i, shortName.charCodeAt(i));
  }
  chunks.push({ id: 'HEAD', data: headData });

  // GLOB chunk — global settings (22 bytes, matching real REX2)
  // Structure (from analyzing 8 real REX2 files):
  //   bytes 0-3:   slice count (total SLCE chunks including placeholder)
  //   bytes 4-5:   time sig numerator (4)
  //   bytes 6-7:   time sig denominator (4)
  //   byte  8:     beats per bar (4)
  //   byte  9:     unknown (0 in most files)
  //   bytes 10-13: 1000 (0x3e8)
  //   bytes 14-15: 0
  //   bytes 16-19: tempo in milli-BPM
  //   bytes 20-21: flags (0x0100)
  const totalSlceCount = store.slices.length + 1; // +1 for placeholder
  const globData = new ArrayBuffer(22);
  const globView = new DataView(globData);
  globView.setUint32(0, totalSlceCount, false); // slice count
  globView.setUint16(4, 4, false);       // time sig numerator
  globView.setUint16(6, 4, false);       // time sig denominator
  globView.setUint8(8, 4);               // beats per bar
  globView.setUint8(9, 0);               // unknown (0 in most files)
  globView.setUint32(10, 0x3e8, false);  // 1000
  globView.setUint16(14, 0, false);      // unknown
  globView.setUint32(16, tempo, false);  // tempo in milli-BPM
  globView.setUint16(20, 0x0100, false); // flags
  chunks.push({ id: 'GLOB', data: globData });

  // RECY chunk — ReCycle metadata (15 bytes, matching real REX2)
  //   bytes 0-3:   0xbc020000 (format version, same as HEAD)
  //   bytes 4-7:   0x00010000 (constant)
  //   bytes 8-11:  loop length in internal units (0 = unknown)
  //   bytes 12-13: zeros
  //   byte  14:    unknown (varies per file, 0 is safe)
  const recyData = new ArrayBuffer(15);
  const recyView = new DataView(recyData);
  recyView.setUint32(0, 0xbc020000, false); // format version
  recyView.setUint32(4, 0x00010000, false); // constant
  recyView.setUint32(8, 0, false);          // loop length (unknown)
  recyView.setUint8(12, 0);
  recyView.setUint8(13, 0);
  recyView.setUint8(14, 0);
  chunks.push({ id: 'RECY', data: recyData });

  // CAT DEVL — device list container (TRSH + EQ + COMP)
  // Uses the same default settings as real ReCycle files.
  const devlChunks = [];
  // TRSH — threshold settings (7 bytes, matching real REX2)
  const trshData = new ArrayBuffer(7);
  const trshView = new DataView(trshData);
  trshView.setUint8(0, 0x01);
  trshView.setUint8(1, 0x00);
  trshView.setUint8(2, 0x03);
  trshView.setUint8(3, 0x03);
  trshView.setUint8(4, 0xff);
  trshView.setUint8(5, 0x00);
  trshView.setUint8(6, 0x32);
  devlChunks.push({ id: 'TRSH', data: trshData });
  // EQ — equalizer settings (17 bytes, flat, matching real REX2)
  const eqData = new ArrayBuffer(17);
  const eqView2 = new DataView(eqData);
  const eqBytes = [0x00, 0x00, 0x0f, 0x00, 0x64, 0x00, 0x00, 0x03, 0xe8, 0x09, 0xc4, 0x00, 0x00, 0x03, 0xe8, 0x4e, 0x20];
  for (let i = 0; i < 17; i++) eqView2.setUint8(i, eqBytes[i]);
  devlChunks.push({ id: 'EQ  ', data: eqData });
  // COMP — compressor settings (9 bytes, bypassed, matching real REX2)
  const compData = new ArrayBuffer(9);
  const compView2 = new DataView(compData);
  const compBytes = [0x00, 0x00, 0x4d, 0x00, 0x27, 0x00, 0xc3, 0x01, 0x64];
  for (let i = 0; i < 9; i++) compView2.setUint8(i, compBytes[i]);
  devlChunks.push({ id: 'COMP', data: compData });
  chunks.push({ id: 'CAT ', formId: 'DEVL', subChunks: devlChunks });

  // CAT SLCL — slice container with SLCE chunks
  // Real REX2 files always start with a 1-sample placeholder slice:
  //   marker=0, length=1, gain=0x0400, flags=0x00
  // Then real slices follow with gain=0x7fff (unity), flags=0x02
  const slclChunks = [];

  // Placeholder slice (1 sample at position 0)
  const placeholderData = new ArrayBuffer(11);
  const placeholderView = new DataView(placeholderData);
  placeholderView.setUint32(0, 0, false);       // marker = 0
  placeholderView.setUint32(4, 1, false);       // length = 1
  placeholderView.setUint16(8, 0x0400, false);  // gain (low, placeholder)
  placeholderView.setUint8(10, 0x00);           // flags = 0 (placeholder)
  slclChunks.push({ id: 'SLCE', data: placeholderData });

  // Real slices
  for (let i = 0; i < store.slices.length; i++) {
    const slice = store.slices[i];
    const start = slice.start;
    const end = (i < store.slices.length - 1) ? store.slices[i + 1].start : frameCount;
    const length = end - start;
    const slceData = new ArrayBuffer(11);
    const slceView = new DataView(slceData);
    slceView.setUint32(0, start, false);      // marker position
    slceView.setUint32(4, length, false);     // slice length
    slceView.setUint16(8, 0x7fff, false);     // gain (unity = max)
    slceView.setUint8(10, 0x02);              // flags = 2 (normal slice)
    slclChunks.push({ id: 'SLCE', data: slceData });
  }
  chunks.push({ id: 'CAT ', formId: 'SLCL', subChunks: slclChunks });

  // SINF chunk — sample info (18 bytes, matching real REX2)
  const sinfData = new ArrayBuffer(18);
  const sinfView = new DataView(sinfData);
  sinfView.setUint8(0, numChannels);     // channels
  sinfView.setUint8(1, 0x03);            // depthCode = 16-bit
  sinfView.setUint32(2, sampleRate, false); // sample rate
  sinfView.setUint32(6, frameCount, false); // frame count
  // loopStart = first real slice marker (matching real REX2 files)
  const loopStart = store.slices.length > 0 ? store.slices[0].start : 0;
  sinfView.setUint32(10, loopStart, false);  // loop start
  sinfView.setUint32(14, frameCount, false); // loop end
  chunks.push({ id: 'SINF', data: sinfData });

  // SDAT chunk — DWOP-compressed audio
  chunks.push({ id: 'SDAT', data: sdatBytes.buffer.slice(sdatBytes.byteOffset, sdatBytes.byteOffset + sdatBytes.byteLength) });

  // Assemble file — recursively build CAT containers
  const fileBytes = buildRex2File(chunks);

  // Download
  const blob = new Blob([fileBytes], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);
  const a = build('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);
}

// Recursively build REX2 IFF chunks into a byte array.
// Each chunk is either:
//   { id, data }                    — leaf chunk
//   { id: 'CAT ', formId, subChunks } — container chunk
function buildRex2File(topChunks) {
  // Calculate inner size for top-level CAT REX2
  let innerSize = 4; // 'REX2' form id
  for (const chunk of topChunks) {
    innerSize += chunkSize(chunk);
  }

  const totalSize = 8 + innerSize;
  const buf = new Uint8Array(totalSize);
  const view = new DataView(buf.buffer);

  // Write CAT REX2 header
  writeFourCC(view, 0, 'CAT ');
  view.setUint32(4, innerSize, false);
  writeFourCC(view, 8, 'REX2');

  let pos = 12;
  for (const chunk of topChunks) {
    pos = writeChunk(buf, view, pos, chunk);
  }

  return buf;
}

function chunkSize(chunk) {
  if (chunk.id === 'CAT ') {
    // CAT <size> <formId(4)> + sub-chunks
    let size = 4; // form id
    for (const sub of chunk.subChunks) {
      size += chunkSize(sub);
    }
    return 8 + size;
  }
  let s = 8 + chunk.data.byteLength;
  if (chunk.data.byteLength & 1) s++; // word alignment
  return s;
}

function writeChunk(buf, view, pos, chunk) {
  if (chunk.id === 'CAT ') {
    // Calculate container inner size
    let innerSize = 4; // form id
    for (const sub of chunk.subChunks) {
      innerSize += chunkSize(sub);
    }
    writeFourCC(view, pos, 'CAT ');
    view.setUint32(pos + 4, innerSize, false);
    writeFourCC(view, pos + 8, chunk.formId);
    pos += 12;
    for (const sub of chunk.subChunks) {
      pos = writeChunk(buf, view, pos, sub);
    }
    return pos;
  }
  // Leaf chunk
  writeFourCC(view, pos, chunk.id);
  view.setUint32(pos + 4, chunk.data.byteLength, false);
  const srcBytes = new Uint8Array(chunk.data);
  buf.set(srcBytes, pos + 8);
  pos += 8 + chunk.data.byteLength;
  if (chunk.data.byteLength & 1) pos++; // word alignment
  return pos;
}

function writeFourCC(view, offset, str) {
  for (let i = 0; i < 4; i++) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
}

// ============================================================================
// SEND SLICES TO BUCKET
// ============================================================================

async function sendSlicesToBucket() {
  if (!store.slices.length || !breakchopBucket.bucket) {
    alertmessage('No slices or bucket not available');
    return;
  }

  const ctx = getAudioContext();
  const sampleRate = store.audioBuffer.sampleRate;
  const numChannels = store.audioBuffer.numberOfChannels;
  let pcm = null;
  try {
    const { audioBufferToPcm } = require('../../../../shared/src/audio');
    pcm = await audioBufferToPcm(store.audioBuffer);
  } catch (_) {}

  store.slices.forEach((slice, i) => {
    const length = slice.end - slice.start;
    if (length <= 0) return;

    const sliceBuffer = ctx.createBuffer(numChannels, length, sampleRate);
    for (let ch = 0; ch < numChannels; ch++) {
      const srcData = store.audioBuffer.getChannelData(ch);
      sliceBuffer.getChannelData(ch).set(srcData.subarray(slice.start, slice.end));
    }

    const wav = audioBufferToWav(sliceBuffer);

    breakchopBucket.bucket.add({
      source: 'breakchop',
      name: `${store.fileName || 'slice'}_${String(i + 1).padStart(2, '0')}.wav`,
      wavLike: new Uint8Array(wav),
      pcm,
      sampleRate,
      channels: numChannels,
      bitDepth: 16,
    });
  });
}

// ============================================================================
// DOWNLOAD CHOPS AS ZIP
// ============================================================================

async function downloadChopsAsZip() {
  if (!store.slices.length || !store.audioBuffer) {
    alertmessage('Load audio and create slices first');
    return;
  }

  const ctx = getAudioContext();
  const sampleRate = store.audioBuffer.sampleRate;
  const numChannels = store.audioBuffer.numberOfChannels;
  const { downloadZip } = require('client-zip');

  const files = [];
  for (let i = 0; i < store.slices.length; i++) {
    const slice = store.slices[i];
    const length = slice.end - slice.start;
    if (length <= 0) continue;

    const sliceBuffer = ctx.createBuffer(numChannels, length, sampleRate);
    for (let ch = 0; ch < numChannels; ch++) {
      sliceBuffer.getChannelData(ch).set(store.audioBuffer.getChannelData(ch).subarray(slice.start, slice.end));
    }
    const wav = audioBufferToWav(sliceBuffer);
    files.push({
      name: `${store.fileName || 'slice'}_${String(i + 1).padStart(2, '0')}.wav`,
      lastModified: new Date(),
      input: new Blob([wav], { type: 'audio/wav' }),
    });
  }

  // Also include the full sample
  const fullWav = audioBufferToWav(store.audioBuffer);
  files.unshift({
    name: `${store.fileName || 'breakchop'}_full.wav`,
    lastModified: new Date(),
    input: new Blob([fullWav], { type: 'audio/wav' }),
  });

  const blob = await downloadZip(files).blob();
  const url = URL.createObjectURL(blob);
  const a = build('a');
  a.href = url;
  a.download = `${store.fileName || 'breakchop'}_chops.zip`;
  a.click();
  URL.revokeObjectURL(url);
}

// ============================================================================
// DOWNLOAD SEQUENCER LOOP (WAV)
// ============================================================================

async function downloadSequencerLoop() {
  if (!store.audioBuffer) {
    alertmessage('Load an audio file first');
    return;
  }

  const stepDuration = 60 / store.bpm / 4;
  const totalSteps = store.numSteps;
  const totalDuration = stepDuration * totalSteps;
  const sampleRate = store.audioBuffer.sampleRate;
  const numChannels = store.audioBuffer.numberOfChannels;
  const totalSamples = Math.ceil(totalDuration * sampleRate);

  const offlineCtx = new OfflineAudioContext(numChannels, totalSamples, sampleRate);

  for (let step = 0; step < totalSteps; step++) {
    const when = step * stepDuration;
    const padsToPlay = store.pattern[step] || [];
    padsToPlay.forEach((padIndex) => {
      const pad = store.pads[padIndex];
      if (!pad || pad.sliceIndex < 0) return;
      const slice = store.slices[pad.sliceIndex];
      if (!slice) return;
      const length = slice.end - slice.start;
      if (length <= 0) return;

      const subs = store.subSteps[`${step}:${padIndex}`];
      const playTimes = (subs && subs > 1)
        ? Array.from({ length: subs }, (_, i) => when + i * (stepDuration / subs))
        : [when];

      playTimes.forEach((playWhen) => {
        const sliceBuffer = offlineCtx.createBuffer(numChannels, length, sampleRate);
        for (let ch = 0; ch < numChannels; ch++) {
          const srcData = store.audioBuffer.getChannelData(ch);
          const dstData = sliceBuffer.getChannelData(ch);
          if (pad.reverse) {
            for (let i = 0; i < length; i++) dstData[i] = srcData[slice.start + length - 1 - i];
          } else {
            dstData.set(srcData.subarray(slice.start, slice.end));
          }
        }
        const source = offlineCtx.createBufferSource();
        source.buffer = sliceBuffer;
        if (pad.pitch !== 0) source.playbackRate.value = Math.pow(2, pad.pitch / 12);
        const gainNode = offlineCtx.createGain();
        gainNode.gain.setValueAtTime(pad.vol, playWhen);
        const decayTime = (length / sampleRate) * pad.decay;
        gainNode.gain.exponentialRampToValueAtTime(0.001, playWhen + decayTime);
        let pannerNode = null;
        if (pad.pan !== 0) {
          pannerNode = offlineCtx.createStereoPanner();
          pannerNode.pan.value = pad.pan;
        }
        source.connect(gainNode);
        if (pannerNode) { gainNode.connect(pannerNode); pannerNode.connect(offlineCtx.destination); }
        else { gainNode.connect(offlineCtx.destination); }
        source.start(playWhen);
      });
    });
  }

  const renderedBuffer = await offlineCtx.startRendering();
  const wav = audioBufferToWav(renderedBuffer);
  const blob = new Blob([wav], { type: 'audio/wav' });
  const url = URL.createObjectURL(blob);
  const a = build('a');
  a.href = url;
  a.download = `${store.fileName || 'breakchop'}_loop.wav`;
  a.click();
  URL.revokeObjectURL(url);
}

// ============================================================================
// WAVEFORM SCROLLBAR / ZOOM
// ============================================================================

function setupScrollbar() {
  let dragging = false;
  let dragStartX = 0;
  let dragStartViewStart = 0;
  let dragStartViewEnd = 0;

  scrollbarHandle.style.left = '0%';
  scrollbarHandle.style.width = '100%';

  scrollbarHandle.addEventListener('mousedown', (e) => {
    e.preventDefault();
    dragging = true;
    dragStartX = e.clientX;
    dragStartViewStart = store.viewStart;
    dragStartViewEnd = store.viewEnd;
  });

  document.addEventListener('mousemove', (e) => {
    if (!dragging) return;
    const rect = scrollbar.getBoundingClientRect();
    const dx = (e.clientX - dragStartX) / rect.width;
    const range = dragStartViewEnd - dragStartViewStart;
    let newStart = dragStartViewStart + dx;
    let newEnd = dragStartViewEnd + dx;
    if (newStart < 0) { newStart = 0; newEnd = range; }
    if (newEnd > 1) { newEnd = 1; newStart = 1 - range; }
    store.viewStart = newStart;
    store.viewEnd = newEnd;
    updateScrollbarHandle();
    drawWaveform();
  });

  document.addEventListener('mouseup', () => {
    dragging = false;
  });

  // Wheel to zoom
  waveformContainer.addEventListener('wheel', (e) => {
    if (!store.audioBuffer) return;
    e.preventDefault();
    const rect = waveformContainer.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const center = store.viewStart + x * (store.viewEnd - store.viewStart);
    const zoomFactor = e.deltaY > 0 ? 1.2 : 0.8;
    const range = (store.viewEnd - store.viewStart) * zoomFactor;
    store.viewStart = Math.max(0, center - range / 2);
    store.viewEnd = Math.min(1, store.viewStart + range);
    store.viewStart = store.viewEnd - range;
    if (store.viewStart < 0) store.viewStart = 0;
    updateScrollbarHandle();
    drawWaveform();
  }, { passive: false });
}

function updateScrollbarHandle() {
  const leftPct = store.viewStart * 100;
  const widthPct = (store.viewEnd - store.viewStart) * 100;
  scrollbarHandle.style.left = leftPct + '%';
  scrollbarHandle.style.width = widthPct + '%';
}

// ============================================================================
// WAVEFORM CLICK — add slice marker
// ============================================================================

function setupWaveformClick() {
  // Prevent system context menu on the waveform canvas
  waveformCanvas.addEventListener('contextmenu', (e) => e.preventDefault());

  waveformCanvas.addEventListener('click', (e) => {
    if (!store.audioBuffer) return;
    const rect = waveformContainer.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const totalSamples = store.audioBuffer.length;
    const visibleStart = store.viewStart * totalSamples;
    const visibleEnd = store.viewEnd * totalSamples;
    const samplePos = Math.floor(visibleStart + x * (visibleEnd - visibleStart));

    // Add a slice at this position
    // Insert sorted
    let insertIdx = store.slices.findIndex((s) => s.start > samplePos);
    if (insertIdx === -1) insertIdx = store.slices.length;

    // Determine end of this slice (start of next, or end of audio)
    const end = insertIdx < store.slices.length ? store.slices[insertIdx].start : totalSamples;
    // Adjust previous slice end
    if (insertIdx > 0) {
      store.slices[insertIdx - 1].end = samplePos;
    }

    store.slices.splice(insertIdx, 0, { start: samplePos, end, name: `slice_${insertIdx + 1}` });

    // Renumber
    store.slices.forEach((s, i) => (s.name = `slice_${i + 1}`));

    autoAssignPads();
    renderSliceMarkers();
    updateSliceCount();
    renderPads();
    renderSequencer();
  });
}

// ============================================================================
// INIT
// ============================================================================

// Bucket integration — returns { bucket, exportModal, BucketManager, dbGetAll }
const breakchopBucket = initLegacyBucket({
  appName: 'breakchop',
});

// ---- Bucket item drop onto waveform/dropzone ----
// Bucket items are dragged with 'application/x-bucket-item-id' dataTransfer.
// The drop handling is merged into setupFileLoading above so both OS file
// drags and bucket item drags are handled by the same listeners.

// Parse a WAV ArrayBuffer manually into an AudioBuffer.
// Handles 8/16/24/32-bit integer and 32/64-bit float PCM.
// decodeAudioData mangles 24-bit files on some browsers, so we do it ourselves.
function parseWavToAudioBuffer(arrayBuffer, ctx) {
  try {
    const view = new DataView(arrayBuffer);

    // Check RIFF header
    if (view.getUint32(0, false) !== 0x52494646) return null; // 'RIFF'
    if (view.getUint32(8, false) !== 0x57415645) return null; // 'WAVE'

    let offset = 12;
    let audioFormat = 0, numChannels = 0, sampleRate = 0, bitsPerSample = 0;
    let dataOffset = 0, dataLength = 0;

    while (offset + 8 <= arrayBuffer.byteLength) {
      const chunkId = view.getUint32(offset, false);
      const chunkSize = view.getUint32(offset + 4, true);

      if (chunkId === 0x666d7420) { // 'fmt '
        audioFormat = view.getUint16(offset + 8, true);
        numChannels = view.getUint16(offset + 10, true);
        sampleRate = view.getUint32(offset + 12, true);
        // byteRate at offset+16, blockAlign at offset+20
        bitsPerSample = view.getUint16(offset + 22, true);
      } else if (chunkId === 0x64617461) { // 'data'
        dataOffset = offset + 8;
        dataLength = chunkSize;
        break;
      }

      offset += 8 + chunkSize + (chunkSize & 1); // word-aligned
    }

    if (!numChannels || !sampleRate || !bitsPerSample || !dataLength) {
      return null;
    }

    // Only handle PCM (format 1) and IEEE float (format 3)
    if (audioFormat !== 1 && audioFormat !== 3) {
      return null;
    }

    const bytesPerSample = bitsPerSample / 8;
    const totalFrames = Math.floor(dataLength / (bytesPerSample * numChannels));
    if (totalFrames <= 0) return null;

    const audioBuffer = ctx.createBuffer(numChannels, totalFrames, sampleRate);
    const isFloat = audioFormat === 3;

    for (let ch = 0; ch < numChannels; ch++) {
      const channelData = audioBuffer.getChannelData(ch);

      for (let i = 0; i < totalFrames; i++) {
        const sampleOffset = dataOffset + (i * numChannels + ch) * bytesPerSample;

        if (isFloat && bitsPerSample === 32) {
          channelData[i] = view.getFloat32(sampleOffset, true);
        } else if (isFloat && bitsPerSample === 64) {
          channelData[i] = view.getFloat64(sampleOffset, true);
        } else if (bitsPerSample === 8) {
          // 8-bit PCM is unsigned (0-255), center at 128
          channelData[i] = (view.getUint8(sampleOffset) - 128) / 128;
        } else if (bitsPerSample === 16) {
          channelData[i] = view.getInt16(sampleOffset, true) / 32768;
        } else if (bitsPerSample === 24) {
          // 24-bit signed little-endian
          const b0 = view.getUint8(sampleOffset);
          const b1 = view.getUint8(sampleOffset + 1);
          const b2 = view.getUint8(sampleOffset + 2);
          let val = (b2 << 16) | (b1 << 8) | b0;
          if (val & 0x800000) val |= 0xff000000; // sign-extend
          channelData[i] = val / 8388608;
        } else if (bitsPerSample === 32) {
          channelData[i] = view.getInt32(sampleOffset, true) / 2147483648;
        }
      }
    }

    return audioBuffer;
  } catch (err) {
    return null;
  }
}

// Load a sample from a bucket item (item.wav contains WAV data)
async function loadSampleFromBucketItem(item) {
  try {
    if (!item.wav) {
      alertmessage('Bucket item has no audio data');
      return;
    }

    // Skip non-audio items
    if (item.type && item.type !== 'wav' && item.type !== 'audio') {
      alertmessage(`Item type "${item.type}" is not audio`);
      return;
    }

    const ctx = getAudioContext();
    if (ctx.state === 'suspended') await ctx.resume();

    // Ensure we have an ArrayBuffer
    let arrayBuffer;
    if (item.wav instanceof ArrayBuffer) {
      arrayBuffer = item.wav;
    } else if (item.wav instanceof Uint8Array) {
      arrayBuffer = item.wav.buffer.slice(item.wav.byteOffset, item.wav.byteOffset + item.wav.byteLength);
    } else if (item.url) {
      // Fallback: fetch from URL if wav data is missing
      const resp = await fetch(item.url);
      arrayBuffer = await resp.arrayBuffer();
    } else {
      arrayBuffer = item.wav;
    }

    // Parse WAV manually — decodeAudioData mangles 24-bit files
    let audioBuffer = parseWavToAudioBuffer(arrayBuffer, ctx);

    if (!audioBuffer) {
      // Fallback: try decodeAudioData for non-WAV or unsupported formats
      try {
        audioBuffer = await ctx.decodeAudioData(arrayBuffer.slice(0));
      } catch (decodeErr) {
        console.error('[breakchop] decodeAudioData fallback failed:', decodeErr);
        alertmessage('Failed to decode audio from bucket item');
        return;
      }
    }

    if (!audioBuffer || audioBuffer.length === 0) {
      alertmessage('Decoded audio is empty');
      return;
    }

    store.audioBuffer = audioBuffer;
    store.fileName = (item.name || 'bucket_sample').replace(/\.[^/.]+$/, '');

    // Show waveform, hide dropzone
    waveformDropzone.style.display = 'none';
    waveformContainer.style.display = 'block';

    // Reset slices
    store.slices = [];
    store.pads = [];

    // Wait for layout to settle before drawing
    requestAnimationFrame(() => {
      drawWaveform();
      renderSliceMarkers();
      updateSliceCount();
      renderPads();
      renderSequencer();
      // Auto-detect transients on load
      detectTransients();
    });
  } catch (err) {
    console.error('loadSampleFromBucketItem error:', err);
    alertmessage('Failed to load sample from bucket');
  }
}

function init() {
  setupFileLoading();
  setupScrollbar();
  setupWaveformClick();

  // Slice controls
  autoSliceBtn.addEventListener('click', detectTransients);
  clearSlicesBtn.addEventListener('click', clearSlices);
  sensitivitySlider.addEventListener('input', () => {
    if (store.audioBuffer && store.slices.length > 0) detectTransients();
  });
  minSliceSlider.addEventListener('input', () => {
    if (store.audioBuffer && store.slices.length > 0) detectTransients();
  });

  // Sequencer controls — single play/stop toggle
  playSeqBtn.addEventListener('click', () => {
    if (store.isPlaying) {
      stopSequencer();
      playSeqBtn.classList.remove('active');
      playSeqBtn.innerHTML = '&#xe800;';
    } else {
      startSequencer();
      playSeqBtn.classList.add('active');
      playSeqBtn.innerHTML = '&#xe801;';
    }
  });
  randomSeqBtn.addEventListener('click', randomizePattern);
  clearSeqBtn.addEventListener('click', clearPattern);

  fillSlider.addEventListener('input', () => {
    fillValEl.textContent = fillSlider.value + '%';
  });
  chaosSlider.addEventListener('input', () => {
    chaosValEl.textContent = chaosSlider.value;
  });

  bpmInput.addEventListener('change', () => {
    store.bpm = parseInt(bpmInput.value) || 120;
  });
  stepsSelect.addEventListener('change', () => {
    const steps = parseInt(stepsSelect.value);
    initPattern(steps);
    renderSequencer();
  });

  // Save modal
  saveBtn.addEventListener('click', () => { saveModal.hidden = false; });
  saveModalClose.addEventListener('click', () => { saveModal.hidden = true; });
  saveModalCancel.addEventListener('click', () => { saveModal.hidden = true; });
  saveModal.addEventListener('click', (e) => { if (e.target === saveModal) saveModal.hidden = true; });
  saveModal.querySelectorAll('.save-modal-option').forEach((btn) => {
    btn.addEventListener('click', () => {
      const action = btn.dataset.action;
      saveModal.hidden = true;
      handleSaveAction(action);
    });
  });

  // Pad params modal close
  qs('#ppm-close').addEventListener('click', closePadParams);
  qs('#ppm-cancel').addEventListener('click', closePadParams);

  // Window resize — redraw waveform
  let resizeTimer = null;
  window.addEventListener('resize', () => {
    if (resizeTimer) clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      if (store.audioBuffer) drawWaveform();
    }, 200);
  });

  // Initial render
  renderPads();
  renderSequencer();
}

init();
